import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { DollarSign, ArrowUpDown, TrendingUp, TrendingDown, RefreshCw, Wifi, WifiOff, Globe } from 'lucide-react';
import { CurrencyScanner } from './CurrencyScanner';
import { ExpenseSharing } from './ExpenseSharing';
import { BudgetTrackingDashboard } from './BudgetTrackingDashboard';
import { useSubscription } from '@/hooks/useSubscription';
import { exchangeRateService, formatCurrency as formatCurrencyUtil, formatLastUpdated } from '@/utils/exchangeRates';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
const POPULAR_CURRENCIES = [{
  code: 'USD',
  name: 'US Dollar',
  symbol: '$'
}, {
  code: 'EUR',
  name: 'Euro',
  symbol: '€'
}, {
  code: 'JPY',
  name: 'Japanese Yen',
  symbol: '¥'
}, {
  code: 'GBP',
  name: 'Pound Sterling',
  symbol: '£'
}, {
  code: 'AUD',
  name: 'Australian Dollar',
  symbol: 'A$'
}, {
  code: 'CAD',
  name: 'Canadian Dollar',
  symbol: 'C$'
}, {
  code: 'CHF',
  name: 'Swiss Franc',
  symbol: 'Fr'
}, {
  code: 'CNY',
  name: 'Chinese Yuan',
  symbol: '¥'
}, {
  code: 'HKD',
  name: 'Hong Kong Dollar',
  symbol: 'HK$'
}, {
  code: 'NZD',
  name: 'New Zealand Dollar',
  symbol: 'NZ$'
}, {
  code: 'KRW',
  name: 'South Korean Won',
  symbol: '₩'
}, {
  code: 'INR',
  name: 'Indian Rupee',
  symbol: '₹'
}, {
  code: 'SGD',
  name: 'Singapore Dollar',
  symbol: 'S$'
}];
export function CurrencyConverter() {
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLiveRates, setIsLiveRates] = useState(true);
  const [rateChange, setRateChange] = useState<number | null>(null);
  const [multiCurrencyResults, setMultiCurrencyResults] = useState<Record<string, number>>({});
  const convertCurrency = async (forceRefresh = false) => {
    if (!amount || !fromCurrency) return;
    setLoading(true);
    try {
      if (forceRefresh) {
        await exchangeRateService.forceRefresh();
        toast.success('Exchange rates refreshed successfully!');
      }

      // Single currency conversion
      if (toCurrency) {
        const rate = await exchangeRateService.getRate(fromCurrency, toCurrency);
        const result = parseFloat(amount) * rate;
        setExchangeRate(rate);
        setConvertedAmount(result);
        setRateChange(exchangeRateService.getRateChange(fromCurrency, toCurrency));
      }

      // Multi-currency conversion
      const allRates = await exchangeRateService.getRates(fromCurrency);
      const results: Record<string, number> = {};
      const amountNum = parseFloat(amount);
      POPULAR_CURRENCIES.forEach(currency => {
        if (currency.code !== fromCurrency && allRates[currency.code]) {
          results[currency.code] = amountNum * allRates[currency.code];
        }
      });
      setMultiCurrencyResults(results);
      setIsLiveRates(exchangeRateService.isUsingLiveRates());
    } catch (error) {
      console.error('Error converting currency:', error);
      toast.error('Failed to refresh rates. Using cached data.');
    } finally {
      setLoading(false);
    }
  };
  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setConvertedAmount(null);
    setExchangeRate(null);
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (amount && fromCurrency && toCurrency) {
        convertCurrency();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [amount, fromCurrency, toCurrency]);
  const formatCurrency = (value: number, currencyCode: string) => {
    return formatCurrencyUtil(value, currencyCode);
  };
  const handleScanResult = (result: any) => {
    setAmount(result.detectedAmount);
    setFromCurrency(result.detectedCurrency);
    // The conversion will happen automatically due to the useEffect
  };
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Currency Converter
        </CardTitle>
        <CardDescription>
          Live exchange rates from ECB, updated every 30 minutes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single">Single Currency</TabsTrigger>
            <TabsTrigger value="multi">
              <Globe className="h-4 w-4 mr-2" />
              Multi-Currency
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="single" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
            {/* From Currency */}
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input type="number" placeholder="100" value={amount} onChange={e => setAmount(e.target.value)} className="text-base sm:text-sm touch-manipulation" inputMode="decimal" />
              <Select value={fromCurrency} onValueChange={setFromCurrency}>
                <SelectTrigger className="touch-manipulation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code} className="touch-manipulation">
                      <span className="font-mono text-sm">{currency.symbol}</span> {currency.code} - <span className="hidden sm:inline">{currency.name}</span>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center order-last lg:order-none">
              <Button variant="outline" size="icon" onClick={swapCurrencies} className="rounded-full touch-manipulation w-12 h-12 lg:w-10 lg:h-10">
                <ArrowUpDown className="h-5 w-5 lg:h-4 lg:w-4" />
              </Button>
            </div>

            {/* To Currency */}
            <div className="space-y-2">
              <Label>Converted Amount</Label>
              <div className="h-12 lg:h-10 px-3 py-2 border rounded-md bg-muted flex items-center justify-center touch-manipulation">
                {loading ? <LoadingSpinner size="sm" /> : convertedAmount !== null ? <span className="font-medium text-sm lg:text-base break-all">
                    {formatCurrency(convertedAmount, toCurrency)}
                  </span> : <span className="text-muted-foreground">-</span>}
              </div>
              <Select value={toCurrency} onValueChange={setToCurrency}>
                <SelectTrigger className="touch-manipulation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code} className="touch-manipulation">
                      <span className="font-mono text-sm">{currency.symbol}</span> {currency.code} - <span className="hidden sm:inline">{currency.name}</span>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

        {exchangeRate && <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-muted rounded-lg gap-2 sm:gap-0">
            <div className="flex items-center gap-2 flex-wrap">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm break-all">
                1 {fromCurrency} = {exchangeRate.toFixed(4)} {toCurrency}
              </span>
              {rateChange !== null && Math.abs(rateChange) > 0.01 && <Badge variant={rateChange > 0 ? "default" : "secondary"} className="text-xs gap-1">
                  {rateChange > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(rateChange).toFixed(2)}%
                </Badge>}
            </div>
            <div className="flex items-center gap-2 justify-start sm:justify-end flex-wrap">
              {isLiveRates ? <Badge variant="outline" className="text-xs gap-1">
                  <Wifi className="h-3 w-3" />
                  Live Rate
                </Badge> : <Badge variant="secondary" className="text-xs gap-1">
                  <WifiOff className="h-3 w-3" />
                  Cached
                </Badge>}
              <span className="text-xs text-muted-foreground">
                {formatLastUpdated(exchangeRateService.getLastUpdated())}
              </span>
            </div>
          </div>}

          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs sm:text-sm text-muted-foreground">
                💡 Live rates from ECB & central banks • Updates every 30 min
              </p>
              <Button variant="ghost" size="sm" onClick={() => convertCurrency(true)} disabled={loading} className="text-xs sm:text-sm touch-manipulation min-h-[44px] w-fit self-start sm:self-auto px-4">
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Updating...' : 'Refresh Live Rates'}
              </Button>
            </div>
          </div>
          </TabsContent>
          
          <TabsContent value="multi" className="space-y-6 mt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input type="number" placeholder="100" value={amount} onChange={e => setAmount(e.target.value)} className="text-base sm:text-sm" inputMode="decimal" />
                </div>
                <div className="space-y-2">
                  <Label>From Currency</Label>
                  <Select value={fromCurrency} onValueChange={setFromCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border z-50">
                      {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code}>
                          <span className="font-mono text-sm">{currency.symbol}</span> {currency.code} - {currency.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {loading ? <div className="col-span-full flex justify-center py-8">
                    <LoadingSpinner size="lg" text="Converting to all currencies..." />
                  </div> : Object.entries(multiCurrencyResults).map(([code, value]) => {
                const currency = POPULAR_CURRENCIES.find(c => c.code === code);
                if (!currency) return null;
                return <Card key={code} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl">{currency.symbol}</span>
                          <Badge variant="outline" className="text-xs">
                            {currency.code}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-lg font-bold break-all">
                            {formatCurrency(value, code)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {currency.name}
                          </p>
                        </div>
                      </Card>;
              })}
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  {isLiveRates ? <Badge variant="outline" className="text-xs gap-1">
                      <Wifi className="h-3 w-3" />
                      Live Rates
                    </Badge> : <Badge variant="secondary" className="text-xs gap-1">
                      <WifiOff className="h-3 w-3" />
                      Cached
                    </Badge>}
                  <span className="text-xs text-muted-foreground">
                    {formatLastUpdated(exchangeRateService.getLastUpdated())}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => convertCurrency(true)} disabled={loading} className="text-xs">
                  <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>;
}
export function CurrencyConverterWithScanner() {
  const [amount, setAmount] = useState('100');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLiveRates, setIsLiveRates] = useState(true);
  const [rateChange, setRateChange] = useState<number | null>(null);
  const [multiCurrencyResults, setMultiCurrencyResults] = useState<Record<string, number>>({});
  const convertCurrency = async (forceRefresh = false) => {
    if (!amount || !fromCurrency) return;
    setLoading(true);
    try {
      if (forceRefresh) {
        await exchangeRateService.forceRefresh();
        toast.success('Exchange rates refreshed successfully!');
      }

      // Single currency conversion
      if (toCurrency) {
        const rate = await exchangeRateService.getRate(fromCurrency, toCurrency);
        const result = parseFloat(amount) * rate;
        setExchangeRate(rate);
        setConvertedAmount(result);
        setRateChange(exchangeRateService.getRateChange(fromCurrency, toCurrency));
      }

      // Multi-currency conversion
      const allRates = await exchangeRateService.getRates(fromCurrency);
      const results: Record<string, number> = {};
      const amountNum = parseFloat(amount);
      POPULAR_CURRENCIES.forEach(currency => {
        if (currency.code !== fromCurrency && allRates[currency.code]) {
          results[currency.code] = amountNum * allRates[currency.code];
        }
      });
      setMultiCurrencyResults(results);
      setIsLiveRates(exchangeRateService.isUsingLiveRates());
    } catch (error) {
      console.error('Error converting currency:', error);
      toast.error('Failed to refresh rates. Using cached data.');
    } finally {
      setLoading(false);
    }
  };
  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setConvertedAmount(null);
    setExchangeRate(null);
  };
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (amount && fromCurrency && toCurrency) {
        convertCurrency();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [amount, fromCurrency, toCurrency]);
  const formatCurrency = (value: number, currencyCode: string) => {
    return formatCurrencyUtil(value, currencyCode);
  };
  const handleScanResult = (result: any) => {
    setAmount(result.detectedAmount);
    setFromCurrency(result.detectedCurrency);
    // The conversion will happen automatically due to the useEffect
  };
  const seoTitle = "Real-Time Currency Converter | Live Exchange Rates for Travel - OdyVoy";
  const seoDescription = "Convert currencies instantly with live exchange rates updated every 30 minutes. Support for USD, EUR, GBP, JPY and 10+ major currencies. Perfect for international travel budget planning.";
  const seoKeywords = "currency converter, exchange rates, live currency conversion, travel money converter, USD to EUR, international currency, foreign exchange, travel budget calculator";
  const canonicalUrl = "https://www.odyvoy.com/dashboard#currency-converter";

  // JSON-LD structured data for currency converter
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "OdyVoy Currency Converter",
    "applicationCategory": "FinanceApplication",
    "description": seoDescription,
    "url": canonicalUrl,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Real-time exchange rates",
      "Multi-currency conversion",
      "Support for 13+ major currencies",
      "Live rate updates every 30 minutes",
      "Historical rate change tracking",
      "Offline cached rates"
    ],
    "provider": {
      "@type": "Organization",
      "name": "OdyVoy",
      "url": "https://www.odyvoy.com"
    }
  };

  return (
    <>
      <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:site_name" content="OdyVoy" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>

    <section className="space-y-6" aria-label="Currency conversion tools">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-50 via-teal-50/50 to-background p-6 sm:p-8 dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-background">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl" aria-hidden="true" />
        <div className="relative flex items-start gap-4">
          <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-lg shadow-cyan-500/30">
            <DollarSign className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">Currency Converter</h1>
            <p className="mt-1 text-sm sm:text-base text-muted-foreground">
              Live exchange rates refreshed every 30 minutes
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {isLiveRates ? (
                <Badge className="gap-1 bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0 hover:opacity-90">
                  <Wifi className="h-3 w-3" /> Live
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <WifiOff className="h-3 w-3" /> Cached
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {formatLastUpdated(exchangeRateService.getLastUpdated())}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Currency Converter */}
      <Card className="border-cyan-500/20 shadow-sm">
        <CardContent className="space-y-6 pt-6">
          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-cyan-50/50 dark:bg-cyan-950/20">
              <TabsTrigger value="single" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-white">Single Currency</TabsTrigger>
              <TabsTrigger value="multi" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-white">
                <Globe className="h-4 w-4 mr-2" />
                Multi-Currency
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="single" className="space-y-6 mt-6">
          <form className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end" onSubmit={e => e.preventDefault()} role="search">
            {/* From Currency */}
            <div className="space-y-2">
              <Label htmlFor="currency-amount" className="text-sm sm:text-base">Amount to Convert</Label>
              <Input id="currency-amount" type="number" placeholder="100" value={amount} onChange={e => setAmount(e.target.value)} className="text-base h-12 touch-manipulation" inputMode="decimal" aria-label="Enter amount to convert" />
              <Select value={fromCurrency} onValueChange={setFromCurrency}>
                <SelectTrigger className="touch-manipulation h-12" aria-label="Select source currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code} className="touch-manipulation min-h-[44px]">
                      <span className="font-mono text-sm" aria-hidden="true">{currency.symbol}</span> {currency.code} - <span className="hidden sm:inline">{currency.name}</span>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Swap Button */}
            <div className="flex justify-center order-last lg:order-none">
              <Button variant="outline" size="icon" onClick={swapCurrencies} className="rounded-full touch-manipulation min-w-[44px] min-h-[44px] w-12 h-12 lg:w-11 lg:h-11 border-cyan-500/40 bg-gradient-to-br from-cyan-50 to-teal-50 hover:from-cyan-100 hover:to-teal-100 dark:from-cyan-950/40 dark:to-teal-950/40 dark:hover:from-cyan-900/40 dark:hover:to-teal-900/40 text-cyan-700 dark:text-cyan-300 shadow-sm" aria-label="Swap currencies" type="button">
                <ArrowUpDown className="h-5 w-5 lg:h-4 lg:w-4" aria-hidden="true" />
              </Button>
            </div>

            {/* To Currency */}
            <div className="space-y-2">
              <Label htmlFor="converted-result" className="text-sm sm:text-base">Converted Result</Label>
              <div id="converted-result" className="min-h-[44px] h-12 px-3 py-2 rounded-md bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-950/40 dark:to-teal-950/40 border border-cyan-500/30 flex items-center touch-manipulation" role="status" aria-live="polite" aria-label={convertedAmount !== null ? `Converted amount: ${formatCurrency(convertedAmount, toCurrency)}` : 'No conversion result yet'}>
                {loading ? <span className="text-muted-foreground text-sm">Converting...</span> : convertedAmount !== null ? <span className="font-semibold text-base break-all bg-gradient-to-r from-cyan-600 to-teal-600 dark:from-cyan-300 dark:to-teal-300 bg-clip-text text-transparent">
                    {formatCurrency(convertedAmount, toCurrency)}
                  </span> : <span className="text-muted-foreground">-</span>}
              </div>
              <Select value={toCurrency} onValueChange={setToCurrency}>
                <SelectTrigger className="touch-manipulation h-12" aria-label="Select target currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background border border-border z-50">
                  {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code} className="touch-manipulation min-h-[44px]">
                      <span className="font-mono text-sm" aria-hidden="true">{currency.symbol}</span> {currency.code} - <span className="hidden sm:inline">{currency.name}</span>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </form>

          {exchangeRate && <aside className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 rounded-xl bg-gradient-to-r from-cyan-50 via-teal-50/60 to-cyan-50 dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-cyan-950/30 border border-cyan-500/20 gap-2 sm:gap-0" aria-label="Exchange rate information">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white">
                  <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span className="text-sm break-all">
                  <span className="text-muted-foreground">1 {fromCurrency} =</span>{' '}
                  <span className="font-semibold text-foreground">{exchangeRate.toFixed(4)} {toCurrency}</span>
                </span>
                {rateChange !== null && Math.abs(rateChange) > 0.01 && <Badge variant={rateChange > 0 ? "default" : "secondary"} className="text-xs gap-1">
                    {rateChange > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {Math.abs(rateChange).toFixed(2)}%
                  </Badge>}
              </div>
              <div className="flex items-center gap-2 justify-start sm:justify-end flex-wrap">
                {isLiveRates ? <Badge className="text-xs gap-1 bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0">
                    <Wifi className="h-3 w-3" />
                    Live Rate
                  </Badge> : <Badge variant="secondary" className="text-xs gap-1">
                    <WifiOff className="h-3 w-3" />
                    Cached
                  </Badge>}
                <span className="text-xs text-muted-foreground">
                  {formatLastUpdated(exchangeRateService.getLastUpdated())}
                </span>
              </div>
            </aside>}

          <footer className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              
              <Button variant="ghost" size="sm" onClick={() => convertCurrency(true)} disabled={loading} className="text-xs touch-manipulation w-fit self-start sm:self-auto" aria-label="Refresh exchange rates" type="button">
                <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                {loading ? 'Updating...' : 'Refresh Live Rates'}
              </Button>
            </div>
          </footer>
            </TabsContent>
            
            <TabsContent value="multi" className="space-y-6 mt-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm sm:text-base">Amount</Label>
                  <Input type="number" placeholder="100" value={amount} onChange={e => setAmount(e.target.value)} className="text-base h-12 touch-manipulation" inputMode="decimal" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm sm:text-base">From Currency</Label>
                  <Select value={fromCurrency} onValueChange={setFromCurrency}>
                    <SelectTrigger className="h-12 touch-manipulation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border z-50">
                      {POPULAR_CURRENCIES.map(currency => <SelectItem key={currency.code} value={currency.code} className="min-h-[44px] touch-manipulation">
                          <span className="font-mono text-sm">{currency.symbol}</span> {currency.code} - {currency.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {loading ? <div className="col-span-full text-center py-8 text-muted-foreground">
                      Converting to all currencies...
                    </div> : Object.entries(multiCurrencyResults).map(([code, value]) => {
                  const currency = POPULAR_CURRENCIES.find(c => c.code === code);
                  if (!currency) return null;
                  return <Card key={code} className="p-4 border-cyan-500/20 hover:border-cyan-500/50 hover:shadow-md hover:shadow-cyan-500/10 transition-all bg-gradient-to-br from-background to-cyan-50/30 dark:to-cyan-950/10">
                          <div className="flex items-center justify-between mb-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white text-lg font-semibold shadow-sm">
                              {currency.symbol}
                            </span>
                            <Badge variant="outline" className="text-xs border-cyan-500/30">
                              {currency.code}
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            <p className="text-lg font-bold break-all bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-300 dark:to-teal-300 bg-clip-text text-transparent">
                              {formatCurrency(value, code)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {currency.name}
                            </p>
                          </div>
                        </Card>;
                })}
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    {isLiveRates ? <Badge variant="outline" className="text-xs gap-1">
                        <Wifi className="h-3 w-3" />
                        Live Rates
                      </Badge> : <Badge variant="secondary" className="text-xs gap-1">
                        <WifiOff className="h-3 w-3" />
                        Cached
                      </Badge>}
                    <span className="text-xs text-muted-foreground">
                      {formatLastUpdated(exchangeRateService.getLastUpdated())}
                    </span>
                  </div>
                <Button variant="ghost" size="sm" onClick={() => convertCurrency(true)} disabled={loading} className="text-xs sm:text-sm min-h-[44px] touch-manipulation px-4">
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Currency Transformer */}
      <CurrencyScanner targetCurrency={toCurrency} onScanResult={handleScanResult} />
      
      {/* Expense Sharing */}
      <ExpenseSharing />

      {/* Budget Tracking */}
      <BudgetSection />
      </section>
    </>
  );
}

function BudgetSection() {
  const { hasFeature } = useSubscription();
  if (!hasFeature('budget_tracking')) return null;
  return (
    <Card className="border-cyan-500/20 overflow-hidden">
      <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
              <TrendingUp className="h-5 w-5" />
            </span>
            <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Budget Tracking
            </span>
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Set category budgets and track spending across your trips
          </CardDescription>
        </CardHeader>
      </div>
      <CardContent className="p-4 sm:p-6">
        <BudgetTrackingDashboard />
      </CardContent>
    </Card>
  );
}